import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiChevronLeft, FiChevronRight, FiX } from 'react-icons/fi';
import './HR.css';

const SHIFT_TYPES = {
  morning:   { label:'Morning',   color:'#f59e0b', bg:'#fef9c3', time:'09:00–17:00' },
  afternoon: { label:'Afternoon', color:'#3b82f6', bg:'#dbeafe', time:'13:00–21:00' },
  evening:   { label:'Evening',   color:'#8b5cf6', bg:'#ede9fe', time:'17:00–01:00' },
  night:     { label:'Night',     color:'#1e293b', bg:'#f1f5f9', time:'22:00–06:00' },
  flexible:  { label:'Flexible',  color:'#10b981', bg:'#d1fae5', time:'Flex' },
  off:       { label:'Off',       color:'#94a3b8', bg:'#f8fafc', time:'—' },
  wfh:       { label:'WFH',       color:'#0ea5e9', bg:'#e0f2fe', time:'WFH' },
};

function getMonthDays(year, month) {
  const first = new Date(year, month, 1).getDay();
  const days  = new Date(year, month + 1, 0).getDate();
  return { first, days };
}

function toISO(d) { return d.toISOString().slice(0, 10); }

export default function Shifts() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const now = new Date();
  const [year,    setYear]    = useState(now.getFullYear());
  const [month,   setMonth]   = useState(now.getMonth());
  const [shifts,  setShifts]  = useState([]);
  const [members, setMembers] = useState([]);
  const [modal,   setModal]   = useState(false);
  const [form,    setForm]    = useState({ userIds:[], dates:[], shiftType:'morning', shiftName:'', startTime:'09:00', endTime:'17:00', notes:'' });
  const [selDates, setSelDates] = useState([]);

  useEffect(() => {
    if (isAdmin) api.get('/users').then(r => setMembers((r.data.users||[]).filter(u=>u.role!=='client')));
    load();
  }, [year, month]);

  const load = async () => {
    try {
      const m = String(month + 1).padStart(2, '0');
      const params = { month: `${year}-${m}` };
      if (!isAdmin) params.userId = user.id;
      const r = await api.get('/shifts', { params });
      setShifts(r.data.shifts || []);
    } catch { toast.error('Failed to load shifts'); }
  };

  const prevMonth = () => { if (month === 0) { setYear(y=>y-1); setMonth(11); } else setMonth(m=>m-1); };
  const nextMonth = () => { if (month === 11) { setYear(y=>y+1); setMonth(0); } else setMonth(m=>m+1); };

  const { first, days } = getMonthDays(year, month);

  const getShift = (userId, day) => {
    const date = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    return shifts.find(s => String(s.userId) === String(userId) && s.date === date);
  };

  const toggleDate = (day) => {
    const date = `${year}-${String(month+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
    setSelDates(prev => prev.includes(date) ? prev.filter(d=>d!==date) : [...prev, date]);
  };

  const openModal = () => {
    setSelDates([]);
    setForm({ userIds:[], dates:[], shiftType:'morning', shiftName:'', startTime:'09:00', endTime:'17:00', notes:'' });
    setModal(true);
  };

  const saveBulk = async () => {
    if (!form.userIds.length || !selDates.length) return toast.error('Select employees and dates');
    try {
      await api.post('/shifts/bulk', { ...form, userIds: form.userIds, dates: selDates });
      toast.success(`Shifts assigned!`);
      setModal(false);
      load();
    } catch { toast.error('Failed to save shifts'); }
  };

  const deleteShift = async (id) => {
    try {
      await api.delete(`/shifts/${id}`);
      setShifts(prev => prev.filter(s => s.id !== id));
    } catch { toast.error('Failed'); }
  };

  const DAYS = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const displayMembers = isAdmin ? members : [user];

  return (
    <div className="hr-page">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h1 style={{ margin:0 }}>Shift / Roster Management</h1>
          <p style={{ margin:'4px 0 0', color:'#94a3b8', fontSize:13 }}>Assign and view employee shifts</p>
        </div>
        {isAdmin && <button onClick={openModal} style={{ padding:'10px 20px', background:'var(--pm-gradient)', color:'#fff', border:'none', borderRadius:10, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}><FiPlus/> Assign Shift</button>}
      </div>

      {/* Month Nav */}
      <div style={{ display:'flex', alignItems:'center', gap:16, marginBottom:20 }}>
        <button onClick={prevMonth} style={{ padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:10, background:'#fff', cursor:'pointer' }}><FiChevronLeft/></button>
        <span style={{ fontWeight:700, fontSize:18, minWidth:200, textAlign:'center' }}>{MONTHS[month]} {year}</span>
        <button onClick={nextMonth} style={{ padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:10, background:'#fff', cursor:'pointer' }}><FiChevronRight/></button>
      </div>

      {/* Roster Grid */}
      <div style={{ overflowX:'auto' }}>
        <table style={{ borderCollapse:'collapse', minWidth:800, width:'100%', fontSize:12 }}>
          <thead>
            <tr style={{ background:'#f8fafc' }}>
              <th style={{ padding:'10px 14px', textAlign:'left', fontWeight:700, color:'#475569', fontSize:12, borderBottom:'2px solid #f1f5f9', position:'sticky', left:0, background:'#f8fafc', zIndex:1, minWidth:150 }}>Employee</th>
              {Array.from({length: days}, (_,i) => {
                const d = new Date(year, month, i+1);
                const isWknd = d.getDay()===0||d.getDay()===6;
                const isToday = toISO(d) === toISO(now);
                return (
                  <th key={i} style={{ padding:'6px 4px', textAlign:'center', fontWeight:700, color: isToday?'#667eea':isWknd?'#f87171':'#475569', borderBottom:'2px solid #f1f5f9', minWidth:42, fontSize:11 }}>
                    <div>{DAYS[d.getDay()]}</div>
                    <div style={{ fontSize:14, fontWeight:800, background: isToday?'#667eea':'transparent', color: isToday?'#fff':'inherit', borderRadius:'50%', width:24, height:24, lineHeight:'24px', textAlign:'center', margin:'2px auto 0' }}>{i+1}</div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {displayMembers.map(m => (
              <tr key={m._id||m.id} style={{ borderBottom:'1px solid #f1f5f9' }}>
                <td style={{ padding:'8px 14px', fontWeight:600, color:'#0f172a', position:'sticky', left:0, background:'#fff', zIndex:1, borderBottom:'1px solid #f1f5f9' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ width:28, height:28, borderRadius:'50%', background:'var(--pm-gradient)', color:'#fff', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:700, flexShrink:0 }}>{m.name.charAt(0)}</div>
                    <div>
                      <div style={{ fontSize:13 }}>{m.name}</div>
                      <div style={{ fontSize:10, color:'#94a3b8', textTransform:'capitalize' }}>{m.role}</div>
                    </div>
                  </div>
                </td>
                {Array.from({length: days}, (_,i) => {
                  const shift = getShift(m._id||m.id, i+1);
                  const cfg = shift ? (SHIFT_TYPES[shift.shiftType] || SHIFT_TYPES.morning) : null;
                  return (
                    <td key={i} style={{ padding:3, textAlign:'center', borderBottom:'1px solid #f8fafc' }}>
                      {shift ? (
                        <div title={`${cfg.label}${shift.startTime ? ` ${shift.startTime}–${shift.endTime}` : ''}${shift.notes ? `\n${shift.notes}` : ''}`}
                          style={{ background:cfg.bg, color:cfg.color, borderRadius:6, padding:'3px 2px', fontSize:10, fontWeight:700, cursor: isAdmin ? 'pointer' : 'default', position:'relative' }}
                          onClick={isAdmin ? () => deleteShift(shift.id) : undefined}>
                          {cfg.label.slice(0,3)}
                        </div>
                      ) : (
                        <div style={{ height:24, borderRadius:4, background:'#f8fafc' }}/>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div style={{ display:'flex', gap:10, marginTop:16, flexWrap:'wrap' }}>
        {Object.entries(SHIFT_TYPES).map(([k,v]) => (
          <span key={k} style={{ padding:'4px 12px', borderRadius:20, fontSize:11, fontWeight:700, background:v.bg, color:v.color }}>{v.label} {v.time !== '—' ? `(${v.time})` : ''}</span>
        ))}
        {isAdmin && <span style={{ fontSize:11, color:'#94a3b8', alignSelf:'center' }}>· Click shift cell to remove</span>}
      </div>

      {/* Assign Modal */}
      {modal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 }}
          onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div style={{ background:'#fff', borderRadius:20, padding:28, width:560, maxWidth:'95vw', maxHeight:'90vh', overflowY:'auto', boxShadow:'0 24px 80px #0003' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
              <h3 style={{ margin:0, fontSize:18, fontWeight:800 }}>Assign Shifts</h3>
              <button onClick={()=>setModal(false)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:'#94a3b8' }}><FiX/></button>
            </div>

            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#64748b', marginBottom:6 }}>Select Employees *</label>
            <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:16, maxHeight:150, overflowY:'auto', padding:8, border:'1.5px solid #e2e8f0', borderRadius:10 }}>
              {members.map(m => {
                const sel = form.userIds.includes(String(m._id||m.id));
                return (
                  <span key={m._id||m.id} onClick={() => {
                    const id = String(m._id||m.id);
                    setForm(f => ({ ...f, userIds: sel ? f.userIds.filter(x=>x!==id) : [...f.userIds, id] }));
                  }} style={{ padding:'5px 12px', borderRadius:20, fontSize:12, fontWeight:600, cursor:'pointer', background: sel ? 'var(--pm-gradient)' : '#f1f5f9', color: sel ? '#fff' : '#475569' }}>
                    {m.name}
                  </span>
                );
              })}
            </div>

            <label style={{ display:'block', fontSize:11, fontWeight:700, color:'#64748b', marginBottom:6 }}>Select Dates * (click to toggle)</label>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, marginBottom:16, padding:10, background:'#f8fafc', borderRadius:10 }}>
              {DAYS.map(d=><div key={d} style={{ textAlign:'center', fontSize:10, fontWeight:700, color:'#94a3b8', paddingBottom:4 }}>{d}</div>)}
              {Array.from({length: first}).map((_,i)=><div key={'e'+i}/>)}
              {Array.from({length: days}, (_,i) => {
                const date = `${year}-${String(month+1).padStart(2,'0')}-${String(i+1).padStart(2,'0')}`;
                const sel = selDates.includes(date);
                return (
                  <div key={i} onClick={() => toggleDate(i+1)}
                    style={{ textAlign:'center', padding:'6px 2px', borderRadius:8, cursor:'pointer', fontSize:12, fontWeight:600,
                      background: sel ? 'var(--pm-gradient)' : '#fff', color: sel ? '#fff' : '#0f172a', border:'1.5px solid', borderColor: sel ? 'transparent' : '#e2e8f0' }}>
                    {i+1}
                  </div>
                );
              })}
            </div>
            {selDates.length > 0 && <div style={{ fontSize:11, color:'#667eea', marginBottom:12 }}>{selDates.length} date(s) selected</div>}

            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:16 }}>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Shift Type *
                <select style={{ display:'block', width:'100%', marginTop:4, padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none' }}
                  value={form.shiftType} onChange={e=>{
                    const cfg = SHIFT_TYPES[e.target.value];
                    const [st,et] = cfg?.time?.includes('–') ? cfg.time.split('–') : ['',''];
                    setForm(f=>({...f, shiftType:e.target.value, startTime:st||'', endTime:et||''}));
                  }}>
                  {Object.entries(SHIFT_TYPES).map(([v,c])=><option key={v} value={v}>{c.label} ({c.time})</option>)}
                </select>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Shift Name (optional)
                <input style={{ display:'block', width:'100%', marginTop:4, padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                  value={form.shiftName} onChange={e=>setForm(f=>({...f,shiftName:e.target.value}))} placeholder="e.g. A-Shift"/>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Start Time
                <input type="time" style={{ display:'block', width:'100%', marginTop:4, padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                  value={form.startTime} onChange={e=>setForm(f=>({...f,startTime:e.target.value}))}/>
              </label>
              <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>End Time
                <input type="time" style={{ display:'block', width:'100%', marginTop:4, padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box' }}
                  value={form.endTime} onChange={e=>setForm(f=>({...f,endTime:e.target.value}))}/>
              </label>
            </div>
            <label style={{ fontSize:11, fontWeight:700, color:'#64748b' }}>Notes
              <textarea style={{ display:'block', width:'100%', marginTop:4, padding:'8px 12px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', boxSizing:'border-box', resize:'vertical', minHeight:60 }}
                value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))}/>
            </label>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:20 }}>
              <button onClick={()=>setModal(false)} style={{ padding:'9px 20px', borderRadius:10, border:'1.5px solid #e2e8f0', background:'#fff', fontWeight:600, cursor:'pointer' }}>Cancel</button>
              <button onClick={saveBulk} style={{ padding:'9px 20px', borderRadius:10, border:'none', background:'var(--pm-gradient)', color:'#fff', fontWeight:700, cursor:'pointer' }}>Assign Shifts</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
